import { useState } from "react";

import NationalityFlags from "../common/NationalityFlags";
import { staffRoleLabel } from "../../constants/lastMinuteChoices";

// Staff variant of AgentPlayerCard (TP-MyStaff P1). Cloned from AgentPlayerCard
// and reduced to the fields a staff/coach roster row carries: photo/initials,
// name, a ROLE TAG in the slot where the player card shows its general position,
// nationality flags, an optional club line, and contract-expiry / age slots that
// only render when data is present.
//
// Deliberate cuts vs AgentPlayerCard (logged in the P1 report):
//  - no 3-dot menu (DetailedPositionsControl is player-specific; no staff editor
//    exists yet).
//  - no "List as available" button (ListAsAvailableButton hardcodes
//    compose=player; a staff compose entry point is not wired).
//  - no card-body navigation (no confirmed staff-profile route to link to).

// Humanize a raw staff role token, preferring the shared catalog label, then a
// custom title, then a generic underscore->Title-Case fallback.
const humanizeRole = (staff) => {
  const raw = staff?.staff_type || staff?.role_type || "";
  const catalog = staffRoleLabel(raw);
  if (catalog) return catalog;
  if (staff?.role_title_custom) return staff.role_title_custom;
  if (!raw) return "";
  return String(raw)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// A staff date of birth is OPTIONAL, and rows created while the field still
// carried `default=date.today` are stamped with the day they were made — those
// stamps were never rewritten, because a stamp cannot be told apart from a real
// entry. An age of 0 or less means MISSING, and the card leaves the slot out
// entirely rather than printing "0".
const getAgeFromDOB = (dob) => {
  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age > 0 ? age : null;
};

const calculateContractExpiring = (endDateValue) => {
  if (!endDateValue) return null;
  const today = new Date();
  const endDate = new Date(endDateValue);
  const timeDiff = endDate - today;
  if (timeDiff <= 0) return "Expired";
  const days = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  if (days < 30) return `${days} days`;
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? "s" : ""}`;
  }
  const years = Math.floor(days / 365);
  const remainingMonths = Math.floor((days % 365) / 30);
  if (remainingMonths > 0) {
    return `${years} year${years > 1 ? "s" : ""} ${remainingMonths} month${
      remainingMonths > 1 ? "s" : ""
    }`;
  }
  return `${years} year${years > 1 ? "s" : ""}`;
};

// TP-Coach-Mandate: pill styling per mandate status. Rendered ONLY when the
// roster row carries `mandate_status` (legacy lightweight invitation-derived
// rows without a mandate render exactly as before). The roster annotation
// only serves pending/active mandates (BE scope filter) — anything else
// falls through to the generic gray fallback below (F8: no dedicated
// unreachable styles).
const MANDATE_STATUS_STYLES = {
  active: { label: "Representation active", className: "bg-green-50 text-green-700 border border-green-200" },
  pending: { label: "Representation pending", className: "bg-amber-50 text-amber-700 border border-amber-200" },
};

const mandateStatusPill = (status) => {
  if (!status) return null;
  const key = String(status).toLowerCase();
  return (
    MANDATE_STATUS_STYLES[key] || {
      label: `Representation ${key}`,
      className: "bg-gray-100 text-gray-600 border border-gray-200",
    }
  );
};

const AgentStaffCard = ({ staff }) => {
  const [imageError, setImageError] = useState(false);

  const name =
    staff?.name ||
    [staff?.first_name, staff?.last_name].filter(Boolean).join(" ") ||
    "N/A";
  // photo_url is the roster endpoint's key — needed for external
  // (pre-registration) mandate rows whose photo comes from the TSCoach (F3).
  const photo =
    staff?.profile_image || staff?.player_image || staff?.image || staff?.photo || staff?.photo_url;
  const role = humanizeRole(staff);

  const clubName = staff?.club?.name || staff?.club_name || null;

  const contractRaw = staff?.contract_expires_on || staff?.contract_expiration || null;
  const contractLabel = contractRaw ? calculateContractExpiring(contractRaw) : null;

  // A served age of 0 or less means the same as a missing one — nobody is zero
  // years old — so it must not reach the card either.
  const age =
    staff?.age > 0
      ? staff.age
      : staff?.date_of_birth
        ? getAgeFromDOB(staff.date_of_birth)
        : null;

  // TP-Coach-Mandate mandate-state row (only when a mandate exists on the row)
  const mandatePill = mandateStatusPill(staff?.mandate_status);
  const mandateEndLabel =
    staff?.mandate_status === "active" && staff?.mandate_end_date
      ? calculateContractExpiring(staff.mandate_end_date)
      : null;

  return (
    <div className="bg-white rounded-lg p-6 hover:shadow transition-shadow border border-[#D2D4D880]">
      <div className="flex flex-col items-start gap-4 mb-4">
        <div className="flex justify-between w-full">
          {photo && !imageError ? (
            <img
              src={photo}
              alt={name}
              className="w-16 h-16 rounded-lg object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center text-xl font-bold text-gray-600">
              {name?.charAt(0) || "S"}
            </div>
          )}
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-[#242426]">{name}</h3>
        </div>
      </div>

      {/* Role tag sits where the player card shows the general position. */}
      <div className="flex bg-[#F7F7F7] p-2 rounded-lg mb-4 items-center gap-2 mt-1 justify-between w-full">
        <span className="text-sm text-[#1A212F] font-medium">{role || "-"}</span>
        {/* All citizenships, national team first. */}
        <NationalityFlags
          nationalities={staff}
          className="w-4 h-3"
          wrapperClassName="flex flex-wrap items-center gap-1.5"
        />
      </div>

      {/* Mandate-state chip (TP-Coach-Mandate) — reuses the card's pill/row
          patterns; absent for legacy lightweight roster rows. */}
      {mandatePill && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span
            className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${mandatePill.className}`}
          >
            {mandatePill.label}
          </span>
        </div>
      )}

      <div className="space-y-3">
        {mandateEndLabel && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-secondary">Representation expiring in</p>
            <p className="text-sm font-medium text-gray-900">{mandateEndLabel}</p>
          </div>
        )}
        {clubName && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-secondary">Club</p>
            <p className="text-sm font-medium text-gray-900">{clubName}</p>
          </div>
        )}
        {contractLabel && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-secondary">Club contract expiring in</p>
            <p className="text-sm font-medium text-gray-900">{contractLabel}</p>
          </div>
        )}
        {age != null && (
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-secondary">Age</p>
            <p className="text-sm font-medium text-gray-900">{age}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentStaffCard;
